import { Button } from "@/components/common/Button";
import { ProgressBar } from "../bonus/ProgressBar";
import GrandPromoThailand from "../modal/GrandPromoThailand";
import { useEffect, useRef, useState } from "react";
import { useUserStore } from "@/modules/auth/store/user";
import { formatNumber } from "@/modules/auth/utils/format";
import Confetti from "react-confetti";
import { useTranslation } from "react-i18next";
import Skeleton from "@/components/common/Skeleton";

const PromoThailand = () => {
  const { t } = useTranslation();

  const { promoThailand, isPromoThailandLoading } = useUserStore();

  const [isOpen, setOpen] = useState(false);

  const [size, setSize] = useState({ width: 0, height: 0 });

  const confettiRef = useRef<HTMLDivElement>(null);

  const turnover10Lines = 300000;
  const turnover1Line = 30000;
  const turnover1LineAdditional = 20000;

  useEffect(() => {
    if (!confettiRef.current) return;

    const element = confettiRef.current;

    const updateSize = () => {
      const rect = element.getBoundingClientRect();
      setSize({ width: rect.width, height: rect.height });
    };

    updateSize();

    const observer = new ResizeObserver(updateSize);
    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, []);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };
  return (
    <>
      <div className="px-4 pt-3">
        <div className="flex flex-col rounded-xl overflow-hidden shadow-context-menu bg-white">
          <div
            ref={confettiRef}
            className="relative shrink-0 w-full h-[9.0625rem]"
          >
            {isPromoThailandLoading ? (
              <Skeleton className="w-full h-full rounded-none" />
            ) : (
              promoThailand.promoTurnover10 < turnover10Lines &&
              promoThailand.promoTurnover1 < turnover1Line && (
                <img
                  src="/images/promo/yacht.png"
                  alt=""
                  className="shrink-0 w-full h-full object-cover"
                />
              )
            )}

            {!isPromoThailandLoading &&
              (promoThailand.promoTurnover10 >= turnover10Lines ||
                promoThailand.promoTurnover1 >= turnover1Line) && (
                <div className="relative w-full h-full">
                  <Confetti
                    numberOfPieces={50}
                    width={size.width}
                    height={size.height}
                  />
                  <img
                    src="/images/promo/promo-success.png"
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
          </div>

          <div className="w-full flex flex-col p-5 pt-4 gap-3">
            <div className="flex flex-col">
              <p className="font-semibold text-[1.0625rem] leading-5.5 tracking-tighter text-text">
                {t("main-page.promo_thailand.title")}
              </p>
              <p className="text-[0.9375rem] leading-5 tracking-tighter text-subtitle-text">
                {t("main-page.promo_thailand.period")}
              </p>
            </div>

            <div className="flex flex-col gap-2">
              {isPromoThailandLoading ? (
                <>
                  <div className="flex flex-col gap-1 px-3 py-2 rounded-lg bg-magnolia">
                    <div className="flex items-center gap-3">
                      <Skeleton className="w-full h-2" />

                      <Skeleton className="w-15 h-5" />
                    </div>

                    <Skeleton className="w-35 h-5" />
                  </div>

                  <div className="flex flex-col gap-1 px-3 py-2 rounded-lg bg-magnolia">
                    <div className="flex items-center gap-3">
                      <Skeleton className="w-full h-2" />

                      <Skeleton className="w-15 h-5" />
                    </div>

                    <Skeleton className="w-35 h-5" />
                  </div>
                </>
              ) : (
                <>
                  <div
                    className={`flex flex-col gap-1 px-3 py-2 rounded-lg ${
                      promoThailand.promoTurnover10 >= turnover10Lines
                        ? "bg-honey"
                        : "bg-magnolia"
                    }`}
                  >
                    {promoThailand.promoTurnover10 >= turnover10Lines && (
                      <p className="text-[0.9375rem] leading-5 tracking-tighter text-subtitle-text">
                        {promoThailand.promoTurnover1 >=
                        turnover1Line + turnover1LineAdditional
                          ? t("main-page.promo_thailand.congrats_tickets")
                          : t("main-page.promo_thailand.congrats_ticket")}
                      </p>
                    )}
                    <div className="flex items-center gap-3">
                      <ProgressBar
                        value={promoThailand.promoTurnover10}
                        max={turnover10Lines}
                        bg="bg-secondary-fill"
                        bgSuccess="bg-green"
                      />
                      <p className="text-[0.9375rem] leading-5 tracking-tighter text-subtitle-text">
                        ${formatNumber(promoThailand.promoTurnover10)}
                      </p>
                    </div>
                    {promoThailand.promoTurnover10 < turnover10Lines && (
                      <p className="text-[0.9375rem] leading-5 tracking-tighter text-subtitle-text">
                        ${formatNumber(turnover10Lines)}{" "}
                        {t("main-page.promo_thailand.turnover_10")}
                      </p>
                    )}
                  </div>

                  <div
                    className={`flex flex-col gap-1 px-3 py-2 rounded-lg ${
                      promoThailand.promoTurnover1 >= turnover1Line
                        ? "bg-honey"
                        : "bg-magnolia"
                    }`}
                  >
                    {promoThailand.promoTurnover1 >= turnover1Line &&
                      promoThailand.promoTurnover10 < turnover10Lines && (
                        <p className="text-[0.9375rem] leading-5 tracking-tighter text-subtitle-text">
                          {promoThailand.promoTurnover1 >=
                          turnover1Line + turnover1LineAdditional
                            ? t("main-page.promo_thailand.congrats_tickets")
                            : t("main-page.promo_thailand.congrats_ticket")}
                        </p>
                      )}
                    <div className="flex items-top gap-3">
                      <div className="w-full flex items-center pt-1.5">
                        <div className="w-3/5 flex flex-col items-end gap-1">
                          <ProgressBar
                            value={promoThailand.promoTurnover1}
                            max={turnover1Line}
                            bg="bg-secondary-fill"
                            bgSuccess={"bg-green"}
                            rounded="rounded-l-full"
                          />
                          <p className="text-[0.9375rem] leading-5 tracking-tighter text-subtitle-text">
                            30k
                          </p>
                        </div>
                        <div className="w-2/5 flex flex-col items-end gap-1">
                          <ProgressBar
                            value={promoThailand.promoTurnover1 - turnover1Line}
                            max={turnover1LineAdditional}
                            bg="bg-secondary-fill"
                            bgSuccess="bg-green"
                            rounded="rounded-r-full"
                          />
                          <p className="text-[0.9375rem] leading-5 tracking-tighter text-subtitle-text">
                            50k
                          </p>
                        </div>
                      </div>
                      <p className="text-[0.9375rem] leading-5 tracking-tighter text-subtitle-text">
                        ${formatNumber(promoThailand.promoTurnover1)}
                      </p>
                    </div>
                    {promoThailand.promoTurnover1 <
                      turnover1Line + turnover1LineAdditional && (
                      <p className="text-[0.9375rem] leading-5 tracking-tighter text-subtitle-text">
                        $
                        {formatNumber(
                          promoThailand.promoTurnover1 >= turnover1Line
                            ? turnover1Line + turnover1LineAdditional
                            : turnover1Line
                        )}{" "}
                        {t("main-page.promo_thailand.turnover_1")}
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>

            <Button
              onClick={handleOpen}
              text={t("main-page.promo_thailand.about")}
              disabled={isPromoThailandLoading}
            />
          </div>
        </div>
      </div>

      {isOpen && <GrandPromoThailand onClose={handleClose} />}
    </>
  );
};

export default PromoThailand;
